import { useEffect, useState, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useSession, signOut } from "../lib/auth-client.ts";
import { getMe } from "../lib/api.ts";

/**
 * Admin gate: requires a signed-in session AND platform-admin authorization
 * (confirmed via GET /admin/me against the ADMIN_EMAILS allowlist).
 *
 * The effect depends on the stable primitive `userId` (not the `useSession()`
 * object, whose reference changes every render) so it runs only when the signed
 * -in user actually changes — no re-check loop. We intentionally do NOT guard
 * with a ref: under React StrictMode the effect runs twice in dev, and a ref
 * guard would let the first (cancelled) call win and leave us stuck on
 * "loading". The `alive` flag already discards stale results safely.
 */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { data: session, isPending } = useSession();
  const userId = session?.user?.id ?? null;
  const [status, setStatus] = useState<"loading" | "admin" | "denied">("loading");

  useEffect(() => {
    if (isPending) return;
    if (!userId) {
      setStatus("denied");
      return;
    }
    let alive = true;
    setStatus("loading");
    getMe()
      .then((me) => {
        if (alive) setStatus(me.isAdmin ? "admin" : "denied");
      })
      .catch(() => {
        if (alive) setStatus("denied");
      });
    return () => {
      alive = false;
    };
  }, [userId, isPending]);

  if (isPending || (userId && status === "loading")) {
    return (
      <div className="auth-page">
        <div className="loading-stack">
          <span className="spinner spinner-lg" />
          <p>Checking access…</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  if (status === "denied") {
    return (
      <div className="auth-page">
        <div className="auth-wrap animate-fade-in">
          <div className="auth-card" style={{ textAlign: "center" }}>
            <div className="denied-mark">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9" />
                <path d="M15 9l-6 6M9 9l6 6" />
              </svg>
            </div>
            <h2 style={{ margin: "14px 0 6px" }}>Not authorized</h2>
            <p className="muted" style={{ marginBottom: 18 }}>
              This account isn’t on the platform-admin allowlist. If you believe this is a
              mistake, ask an existing admin to add your email to <code>ADMIN_EMAILS</code>.
            </p>
            <button className="btn btn-ghost" onClick={() => signOut().then(() => (window.location.href = "/login"))}>
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
